import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * Budgets flat route tree (EX-39748) — the canonical tabbed-family pattern.
 *
 * This is the ONE place that describes the Budgets tab tree (family tabs +
 * entity sub-tabs). Everything else derives from it:
 *   - the flat-route GATE paths (see `budgetsFamilyGatePaths` / `budgetsEntityGatePaths`),
 *   - the per-tab route METADATA fed to `locationHooks` (sidenav active state,
 *     breadcrumbs, content type) — built in `flatArchitectureRouteMetadata.ts`,
 *   - the tab DESCRIPTORS the page components hand to `<FamilyLayout/>`.
 *
 * To add a tab: add one node to `family.tabs` or `entity.tabs`. The gate,
 * metadata, sidenav highlight, and tab bar all pick it up.
 *
 * Two shapes, two gate strategies (see the gate-path builders below):
 *   - Family tabs are STATIC paths (`/budgets/all`, …). They are enumerated in
 *     the gate so they out-specify the entity `:id` route (`/budgets/:id`) —
 *     a static segment beats a param at equal depth.
 *   - Entity sub-tabs all live under `/budgets/:id`. The gate matches them with
 *     a single param-child pattern (`/budgets/:id/:budgetTab`) so that BOTH
 *     known tabs and unknown children resolve to the entity page, preserving the
 *     entity-scoped not-found fallback the legacy nested `<Switch>` produced.
 */

export interface BudgetsFamilyTabNode {
  /** Stable key used for active-tab detection and FamilyLayout. */
  key: string
  /** Translation key; rendered with <Trns/> in the page. */
  labelSource: string
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/budgets/all`. */
  path: string
}

export interface BudgetsEntityTabNode {
  key: string
  labelSource: string
  appLocation: AppLocation
  /** Exact URL pattern with the `:id` param, e.g. `/budgets/:id/overview`. */
  path: string
  /** Builds the navigable URL for a concrete budget id. */
  to: (id: string) => string
}

export const budgetsRouteTree = {
  family: {
    /** `/budgets` — redirects to the first tab. */
    rootPath: Routes.budgetsUrl(),
    rootAppLocation: AppLocation.BudgetsAll,
    redirectTo: Routes.budgetsAllBudgetsUrl(),
    tabs: [
      {
        key: 'all',
        labelSource: 'Overview',
        appLocation: AppLocation.BudgetsAll,
        path: Routes.budgetsAllBudgetsUrl(),
      },
      {
        key: 'needsapproval',
        labelSource: 'Needs Approval',
        appLocation: AppLocation.BudgetsNeedsApproval,
        path: Routes.budgetsNeedsApprovalUrl(),
      },
    ] as BudgetsFamilyTabNode[],
    /**
     * `/budgets/requests` renders the same content as Needs Approval and folds
     * into its active state (legacy parity). Enumerated here so the family shell
     * — not the entity `:id` gate — owns the path.
     */
    aliasPaths: [
      {
        appLocation: AppLocation.BudgetsNeedsApproval,
        path: Routes.budgetsRequestsUrl(),
      },
    ],
  },
  entity: {
    /** `/budgets/:id` — redirects to the first sub-tab. */
    rootPath: Routes.budgetUrl(':id'),
    rootAppLocation: AppLocation.BudgetDetail,
    /** Param name used by the gate's param-child matcher. */
    tabParam: 'budgetTab',
    redirectTo: (id: string) => Routes.budgetOverviewUrl(id),
    tabs: [
      {
        key: 'overview',
        labelSource: 'Overview',
        appLocation: AppLocation.BudgetOverview,
        path: Routes.budgetOverviewUrl(':id'),
        to: Routes.budgetOverviewUrl,
      },
      {
        key: 'virtualcards',
        labelSource: 'Virtual Cards',
        appLocation: AppLocation.BudgetVirtualCards,
        path: Routes.budgetVirtualCardsUrl(':id'),
        to: Routes.budgetVirtualCardsUrl,
      },
      {
        key: 'transactions',
        labelSource: 'Transactions',
        appLocation: AppLocation.BudgetTransactions,
        path: Routes.budgetTransactionsUrl(':id'),
        to: Routes.budgetTransactionsUrl,
      },
      {
        key: 'history',
        labelSource: 'History',
        appLocation: AppLocation.BudgetHistory,
        path: Routes.budgetHistoryUrl(':id'),
        to: Routes.budgetHistoryUrl,
      },
    ] as BudgetsEntityTabNode[],
  },
}

/**
 * GATE paths for the family page: the family root plus every static tab/alias
 * path. Enumerated (not parameterized) so each static path out-specifies the
 * entity `/budgets/:id` route at equal depth.
 */
export const budgetsFamilyGatePaths: string[] = [
  budgetsRouteTree.family.rootPath,
  ...budgetsRouteTree.family.tabs.map((t) => t.path),
  ...budgetsRouteTree.family.aliasPaths.map((a) => a.path),
]

/**
 * GATE paths for the entity page: the entity root plus a single param-child
 * matcher covering all sub-tabs (and unknown children, for fallback parity).
 *
 * Parity note: this matches the 2-segment entity root and the 3-segment
 * `/budgets/:id/<tab>` (including unknown tabs → entity-scoped NotFound). A
 * malformed DEEPER URL (`/budgets/:id/<tab>/<extra>`, 4+ segments) no longer
 * matches and falls through to the global not-found instead of the legacy
 * in-page entity NotFound. No app-generated link produces such URLs, so this
 * is an accepted, documented delta rather than widening the matcher (which
 * would risk the static-beats-param specificity the family gate depends on).
 */
export const budgetsEntityGatePaths: string[] = [
  budgetsRouteTree.entity.rootPath,
  `${budgetsRouteTree.entity.rootPath}/:${budgetsRouteTree.entity.tabParam}`,
]
